import { Injectable, computed, signal } from '@angular/core';
import {
  CANTEEN_ACCOUNTS,
  CANTEEN_AUDIT,
  CANTEEN_BLOCKS,
  CANTEEN_CASHIER_MOVEMENTS,
  CANTEEN_CASHIER_SESSION,
  CANTEEN_CATEGORIES,
  CANTEEN_CLASSES,
  CANTEEN_GUARDIANS,
  CANTEEN_MENU,
  CANTEEN_NOTICES,
  CANTEEN_ORDERS,
  CANTEEN_PRODUCTS,
  CANTEEN_PROFILES,
  CANTEEN_PROMOTIONS,
  CANTEEN_RESTRICTIONS,
  CANTEEN_SCHOOL,
  CANTEEN_STOCK_MOVEMENTS,
  CANTEEN_STUDENTS,
  CANTEEN_TRANSACTIONS,
  CANTEEN_UNIT,
} from 'core/mocks/canteen.mock';
import {
  CANTEEN_ID,
  CANTEEN_SCHOOL_ID,
  CANTEEN_TENANT_ID,
  CanteenAccount,
  CanteenAuditLog,
  CanteenBlockedProduct,
  CanteenCartLine,
  CanteenCashierMovement,
  CanteenCashierMovementType,
  CanteenCashierSession,
  CanteenCategory,
  CanteenClassGroup,
  CanteenDietaryRestriction,
  CanteenGuardian,
  CanteenMenuSlot,
  CanteenNotice,
  CanteenOrder,
  CanteenOrderStatus,
  CanteenProduct,
  CanteenProfile,
  CanteenPromotion,
  CanteenResult,
  CanteenSchool,
  CanteenStockMovement,
  CanteenStockMovementType,
  CanteenStudent,
  CanteenTransaction,
  CanteenTransactionType,
  CanteenUnit,
  PlaceCanteenOrderDto,
  SaveCanteenLimitsDto,
  SaveCanteenProductDto,
  failResult,
  okResult,
  roundMoney,
  toIsoDate,
} from 'core/models/canteen.model';
import {
  activePromotion,
  canCancelOrder,
  canPickupOrder,
  canRefundOrder,
  consumptionSpend,
  expectedCashierBalance,
  lineTotal,
  resolveUnitPrice,
  slotMatches,
  validatePurchase,
} from 'core/utils/canteen-rules.util';

export interface CanteenCashierSnapshot {
  readonly session: CanteenCashierSession | null;
  readonly movements: readonly CanteenCashierMovement[];
  readonly entries: number;
  readonly sales: number;
  readonly refunds: number;
  readonly withdrawals: number;
  readonly adjustments: number;
  readonly expectedBalance: number;
}

export interface CanteenOperationStats {
  readonly salesToday: number;
  readonly salesMonth: number;
  readonly ordersToday: number;
  readonly averageTicket: number;
  readonly pendingOrders: number;
  readonly scheduledOrders: number;
  readonly pickupOrders: number;
  readonly cancellations: number;
  readonly refunds: number;
  readonly lowStock: number;
}

export interface CanteenReportRow {
  readonly label: string;
  readonly quantity: number;
  readonly total: number;
}

@Injectable({ providedIn: 'root' })
export class CanteenService {
  /** Só sobe o shell após OneAuth (ou escolha explícita do perfil operacional). */
  private readonly signedInState = signal(false);
  private readonly profileIdState = signal('usr-admin');
  private readonly profileNames = signal<Readonly<Record<string, string>>>({});
  private readonly selectedStudentIdState = signal('stu-joao');
  private readonly unitState = signal<CanteenUnit>({ ...CANTEEN_UNIT });
  private readonly studentsState = signal<CanteenStudent[]>([
    ...CANTEEN_STUDENTS,
  ]);
  private readonly accountsState = signal<CanteenAccount[]>(
    CANTEEN_ACCOUNTS.map((item) => ({ ...item })),
  );
  private readonly transactionsState = signal<CanteenTransaction[]>([
    ...CANTEEN_TRANSACTIONS,
  ]);
  private readonly categoriesState = signal<CanteenCategory[]>([
    ...CANTEEN_CATEGORIES,
  ]);
  private readonly productsState = signal<CanteenProduct[]>(
    CANTEEN_PRODUCTS.map((item) => ({
      ...item,
      ingredients: [...item.ingredients],
      allergens: [...item.allergens],
    })),
  );
  private readonly menuState = signal<CanteenMenuSlot[]>(
    CANTEEN_MENU.map((item) => ({ ...item, weekdays: [...item.weekdays] })),
  );
  private readonly promotionsState = signal<CanteenPromotion[]>([
    ...CANTEEN_PROMOTIONS,
  ]);
  private readonly blocksState = signal<CanteenBlockedProduct[]>([
    ...CANTEEN_BLOCKS,
  ]);
  private readonly restrictionsState = signal<CanteenDietaryRestriction[]>([
    ...CANTEEN_RESTRICTIONS,
  ]);
  private readonly ordersState = signal<CanteenOrder[]>(
    CANTEEN_ORDERS.map((item) => ({ ...item, items: [...item.items] })),
  );
  private readonly stockState = signal<CanteenStockMovement[]>([
    ...CANTEEN_STOCK_MOVEMENTS,
  ]);
  private readonly cashierState = signal<CanteenCashierSession | null>({
    ...CANTEEN_CASHIER_SESSION,
  });
  private readonly cashierMovementsState = signal<CanteenCashierMovement[]>([
    ...CANTEEN_CASHIER_MOVEMENTS,
  ]);
  private readonly auditState = signal<CanteenAuditLog[]>([...CANTEEN_AUDIT]);
  private readonly noticesState = signal<CanteenNotice[]>([...CANTEEN_NOTICES]);
  private orderSequence = 125;

  readonly school: CanteenSchool = CANTEEN_SCHOOL;
  readonly classes: readonly CanteenClassGroup[] = CANTEEN_CLASSES;
  readonly guardians: readonly CanteenGuardian[] = CANTEEN_GUARDIANS;
  readonly profiles: readonly CanteenProfile[] = CANTEEN_PROFILES;

  readonly signedIn = this.signedInState.asReadonly();
  readonly unit = this.unitState.asReadonly();
  readonly students = this.studentsState.asReadonly();
  readonly accounts = this.accountsState.asReadonly();
  readonly transactions = this.transactionsState.asReadonly();
  readonly categories = this.categoriesState.asReadonly();
  readonly products = this.productsState.asReadonly();
  readonly menu = this.menuState.asReadonly();
  readonly orders = this.ordersState.asReadonly();
  readonly stockMovements = this.stockState.asReadonly();
  readonly restrictions = this.restrictionsState.asReadonly();
  readonly blocks = this.blocksState.asReadonly();
  readonly auditLogs = this.auditState.asReadonly();
  readonly notices = this.noticesState.asReadonly();
  readonly selectedStudentId = this.selectedStudentIdState.asReadonly();

  readonly profile = computed(() => {
    const base =
      this.profiles.find((item) => item.id === this.profileIdState()) ??
      this.profiles[0];
    const name = this.profileNames()[base.id];
    return name ? { ...base, name } : base;
  });

  renameSignedIn(name: string): void {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    const id = this.profileIdState();
    this.profileNames.update((current) => ({ ...current, [id]: trimmed }));
  }

  readonly role = computed(() => this.profile().role);

  readonly visibleStudents = computed(() => {
    const profile = this.profile();
    const students = this.studentsState();
    if (profile.role === 'STUDENT') {
      return students.filter((item) => item.id === profile.studentId);
    }
    if (profile.role === 'GUARDIAN') {
      const guardian = this.guardians.find(
        (item) => item.id === profile.guardianId,
      );
      const ids = new Set(guardian?.studentIds ?? []);
      return students.filter((item) => ids.has(item.id));
    }
    return students;
  });

  readonly selectedStudent = computed(
    () =>
      this.visibleStudents().find(
        (item) => item.id === this.selectedStudentIdState(),
      ) ??
      this.visibleStudents()[0] ??
      null,
  );

  readonly activeCashier = computed(() =>
    this.cashierState()?.status === 'OPEN' ? this.cashierState() : null,
  );

  readonly lowStockProducts = computed(() =>
    this.productsState().filter(
      (item) => item.stockControlled && item.stock <= item.minStock,
    ),
  );

  readonly operationStats = computed(() => this.buildStats());

  readonly cashierSnapshot = computed(() => this.buildCashier());

  readonly visibleNotices = computed(() => {
    const role = this.role();
    return this.noticesState().filter(
      (item) => item.role === 'ALL' || item.role === role,
    );
  });

  signOut(): void {
    this.signedInState.set(false);
  }

  signIn(profileId = this.profileIdState()): void {
    if (!this.profiles.some((item) => item.id === profileId)) {
      return;
    }
    this.profileIdState.set(profileId);
    this.signedInState.set(true);
    const profile = this.profiles.find((item) => item.id === profileId);
    if (profile?.role === 'STUDENT' && profile.studentId) {
      this.selectedStudentIdState.set(profile.studentId);
      return;
    }
    if (profile?.role === 'GUARDIAN') {
      const guardian = this.guardians.find(
        (item) => item.id === profile.guardianId,
      );
      const first = guardian?.studentIds[0];
      if (first) {
        this.selectedStudentIdState.set(first);
      }
    }
  }

  selectStudent(studentId: string): void {
    if (this.visibleStudents().some((item) => item.id === studentId)) {
      this.selectedStudentIdState.set(studentId);
    }
  }

  canAccessStudent(studentId: string): boolean {
    return this.visibleStudents().some((item) => item.id === studentId);
  }

  className(classId: string): string {
    return this.classes.find((item) => item.id === classId)?.name ?? '-';
  }

  studentName(studentId: string): string {
    return (
      this.studentsState().find((item) => item.id === studentId)?.name ?? '-'
    );
  }

  categoryName(categoryId: string): string {
    return (
      this.categoriesState().find((item) => item.id === categoryId)?.name ?? '-'
    );
  }

  productName(productId: string): string {
    return (
      this.productsState().find((item) => item.id === productId)?.name ?? '-'
    );
  }

  accountFor(studentId: string): CanteenAccount | null {
    return (
      this.accountsState().find((item) => item.studentId === studentId) ?? null
    );
  }

  ordersForSession(): CanteenOrder[] {
    const allowed = new Set(this.visibleStudents().map((item) => item.id));
    const role = this.role();
    return this.ordersState().filter((order) => {
      if (
        role === 'CANTEEN_ADMIN' ||
        role === 'CANTEEN_MANAGER' ||
        role === 'CANTEEN_OPERATOR'
      ) {
        return true;
      }
      return allowed.has(order.studentId);
    });
  }

  orderById(orderId: string): CanteenOrder | null {
    return this.ordersForSession().find((item) => item.id === orderId) ?? null;
  }

  transactionsFor(studentId: string): CanteenTransaction[] {
    if (!this.canAccessStudent(studentId)) {
      return [];
    }
    return this.transactionsState()
      .filter((item) => item.studentId === studentId)
      .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  }

  restrictionsFor(studentId: string): CanteenDietaryRestriction[] {
    if (!this.canAccessStudent(studentId)) {
      return [];
    }
    return this.restrictionsState().filter(
      (item) => item.studentId === studentId,
    );
  }

  blockedIds(studentId: string): string[] {
    return this.blocksState()
      .filter((item) => item.studentId === studentId)
      .map((item) => item.productId);
  }

  menuSlotFor(productId: string, at: Date): CanteenMenuSlot | null {
    return (
      this.menuState().find(
        (item) => item.productId === productId && slotMatches(item, at),
      ) ?? null
    );
  }

  quoteLines(lines: readonly CanteenCartLine[], at: Date) {
    return lines
      .filter((line) => line.quantity > 0)
      .map((line) => {
        const product = this.productsState().find(
          (item) => item.id === line.productId,
        );
        if (!product) {
          return null;
        }
        const slot =
          this.menuState().find((item) => item.productId === product.id) ??
          null;
        const promotion = activePromotion(
          this.promotionsState(),
          product.id,
          at,
        );
        const unitPrice = resolveUnitPrice(product, slot, promotion);
        return {
          product,
          slot,
          quantity: line.quantity,
          unitPrice,
          total: lineTotal(unitPrice, line.quantity),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  explainPurchase(dto: PlaceCanteenOrderDto, now = new Date()): string[] {
    return validatePurchase(this.purchaseContext(dto, now));
  }

  placeOrder(
    dto: PlaceCanteenOrderDto,
    now = new Date(),
  ): CanteenResult<CanteenOrder> {
    if (dto.channel === 'APP' && this.role() === 'STUDENT') {
      if (!this.unitState().allowsStudentOrders) {
        return failResult('Pedidos do aluno estão desabilitados.');
      }
    }

    if (
      (this.role() === 'GUARDIAN' || this.role() === 'STUDENT') &&
      !this.canAccessStudent(dto.studentId)
    ) {
      return failResult('Responsável só acessa alunos vinculados.');
    }

    const reasons = this.explainPurchase(dto, now);
    if (reasons.length > 0) {
      return failResult(reasons.join(' '));
    }

    const at = dto.scheduledFor ? new Date(dto.scheduledFor) : now;
    const quoted = this.quoteLines(dto.lines, at);
    const subtotal = roundMoney(
      quoted.reduce((sum, line) => sum + line.total, 0),
    );
    const catalogTotal = roundMoney(
      quoted.reduce(
        (sum, line) => sum + lineTotal(line.product.price, line.quantity),
        0,
      ),
    );
    const discount = roundMoney(Math.max(0, catalogTotal - subtotal));
    this.orderSequence += 1;
    const number = String(this.orderSequence).padStart(6, '0');
    const order: CanteenOrder = {
      id: createId('ord'),
      number,
      tenantId: CANTEEN_TENANT_ID,
      schoolId: CANTEEN_SCHOOL_ID,
      canteenId: CANTEEN_ID,
      studentId: dto.studentId,
      guardianId: this.profile().guardianId,
      items: quoted.map((line) => ({
        productId: line.product.id,
        productName: line.product.name,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        total: line.total,
      })),
      subtotal: catalogTotal,
      discount,
      total: subtotal,
      paymentMethod: dto.paymentMethod,
      channel: dto.channel,
      createdAt: now.toISOString(),
      scheduledFor: dto.scheduledFor,
      status: dto.scheduledFor ? 'SCHEDULED' : 'PAID',
      qrCode: `MOCK-ORDER-${number}`,
      pickedUpBy: null,
      pickedUpAt: null,
      operatorId: dto.channel === 'POS' ? this.profile().id : null,
    };

    this.ordersState.update((items) => [order, ...items]);
    for (const line of quoted) {
      this.changeStock(
        line.product.id,
        -line.quantity,
        'SALE',
        `Pedido ${number}`,
      );
    }

    if (dto.paymentMethod === 'BALANCE') {
      this.pushBalance(
        dto.studentId,
        'DEBIT',
        subtotal,
        dto.scheduledFor
          ? `Agendamento - ${quoted.map((line) => line.product.name).join(', ')}`
          : `Compra - ${quoted.map((line) => line.product.name).join(', ')}`,
        order.id,
      );
    }

    if (dto.channel === 'POS') {
      this.pushCashier('SALE', subtotal, `Pedido ${number}`);
    }

    this.audit(
      'ORDER_CREATED',
      'order',
      order.id,
      `Pedido ${number} confirmado.`,
    );
    this.pushNotice(
      'Pedido confirmado',
      `Pedido ${number} registrado para ${this.studentName(dto.studentId)}.`,
    );
    return okResult(order);
  }

  cancelOrder(orderId: string, now = new Date()): CanteenResult<CanteenOrder> {
    const order = this.ordersState().find((item) => item.id === orderId);
    if (!order || !this.orderById(orderId)) {
      return failResult('Pedido não encontrado.');
    }
    if (
      !canCancelOrder(
        order,
        this.role(),
        now,
        this.unitState().cancellationWindowHours,
      )
    ) {
      return failResult('Cancelamento comum bloqueado.');
    }

    const next = { ...order, status: 'CANCELLED' as const };
    this.replaceOrder(next);
    for (const item of order.items) {
      this.changeStock(
        item.productId,
        item.quantity,
        'REFUND',
        `Cancelamento ${order.number}`,
      );
    }
    if (order.paymentMethod === 'BALANCE') {
      this.pushBalance(
        order.studentId,
        'CANCELLATION',
        order.total,
        `Cancelamento - pedido ${order.number}`,
        order.id,
      );
    }
    if (order.channel === 'POS') {
      this.pushCashier('REFUND', order.total, `Cancelamento ${order.number}`);
    }
    this.audit(
      'ORDER_CANCELLED',
      'order',
      order.id,
      `Pedido ${order.number} cancelado.`,
    );
    return okResult(next);
  }

  refundOrder(orderId: string): CanteenResult<CanteenOrder> {
    const order = this.ordersState().find((item) => item.id === orderId);
    if (!order || !this.orderById(orderId)) {
      return failResult('Pedido não encontrado.');
    }
    if (!canRefundOrder(order, this.role())) {
      return failResult(
        'Devolução de pagamento não permitida para este perfil.',
      );
    }

    const next = { ...order, status: 'REFUNDED' as const };
    this.replaceOrder(next);
    for (const item of order.items) {
      this.changeStock(
        item.productId,
        item.quantity,
        'REFUND',
        `Devolução ${order.number}`,
      );
    }
    if (order.paymentMethod === 'BALANCE') {
      this.pushBalance(
        order.studentId,
        'REFUND',
        order.total,
        `Devolução - pedido ${order.number}`,
        order.id,
      );
    }
    if (order.channel === 'POS') {
      this.pushCashier('REFUND', order.total, `Devolução ${order.number}`);
    }
    this.audit(
      'REFUND_CREATED',
      'order',
      order.id,
      `Devolução de ${order.total.toFixed(2)} no pedido ${order.number}.`,
    );
    return okResult(next);
  }

  advanceOrder(
    orderId: string,
    status: Extract<
      CanteenOrderStatus,
      'PREPARING' | 'READY' | 'READY_FOR_PICKUP'
    >,
  ): CanteenResult<CanteenOrder> {
    const order = this.orderById(orderId);
    if (!order) {
      return failResult('Pedido não encontrado.');
    }
    const allowed: Partial<Record<CanteenOrderStatus, CanteenOrderStatus[]>> = {
      PREPARING: ['PAID', 'SCHEDULED'],
      READY: ['PREPARING'],
      READY_FOR_PICKUP: ['PREPARING', 'READY'],
    };
    if (!allowed[status]?.includes(order.status)) {
      return failResult('Status do pedido não permite esta etapa.');
    }
    const next = { ...order, status };
    this.replaceOrder(next);
    return okResult(next);
  }

  confirmPickup(
    code: string,
    pickedUpBy: string,
    now = new Date(),
  ): CanteenResult<CanteenOrder> {
    if (!this.unitState().allowsPickup) {
      return failResult('Retirada desabilitada.');
    }
    const normalized = code.trim().toUpperCase();
    const order = this.ordersState().find(
      (item) =>
        item.qrCode.toUpperCase() === normalized ||
        item.number.toUpperCase() === normalized,
    );
    if (!order) {
      return failResult('Pedido não encontrado.');
    }
    if (!canPickupOrder(order)) {
      return failResult('Somente pedidos prontos podem ser retirados.');
    }
    const who = pickedUpBy.trim();
    if (!who) {
      return failResult('Informe quem retirou.');
    }
    const next: CanteenOrder = {
      ...order,
      status: 'DELIVERED',
      pickedUpBy: who,
      pickedUpAt: now.toISOString(),
      operatorId: this.profile().id,
    };
    this.replaceOrder(next);
    this.audit(
      'ORDER_DELIVERED',
      'order',
      order.id,
      `Pedido ${order.number} retirado por ${who}.`,
    );
    return okResult(next);
  }

  addCredit(
    studentId: string,
    amount: number,
    description = 'Crédito manual',
  ): CanteenResult<CanteenAccount> {
    if (this.role() !== 'GUARDIAN' && this.role() !== 'CANTEEN_ADMIN') {
      return failResult('Crédito não permitido para este perfil.');
    }
    if (!this.canAccessStudent(studentId) && this.role() !== 'CANTEEN_ADMIN') {
      return failResult('Responsável só acessa alunos vinculados.');
    }
    if (!this.unitState().allowsBalance) {
      return failResult('Saldo desabilitado.');
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      return failResult('Informe um valor maior que zero.');
    }
    const label = description.trim() || 'Crédito manual';
    const account = this.pushBalance(
      studentId,
      'CREDIT',
      roundMoney(amount),
      label,
      null,
    );
    if (!account) {
      return failResult('Conta de consumo não encontrada.');
    }
    this.audit(
      'BALANCE_CREDITED',
      'account',
      account.id,
      `Crédito de ${amount.toFixed(2)} para ${this.studentName(studentId)}.`,
    );
    return okResult(account);
  }

  saveLimits(
    studentId: string,
    limits: SaveCanteenLimitsDto,
  ): CanteenResult<CanteenAccount> {
    if (this.role() !== 'GUARDIAN' && this.role() !== 'CANTEEN_ADMIN') {
      return failResult('Limites só podem ser alterados pelo responsável.');
    }
    if (!this.canAccessStudent(studentId)) {
      return failResult('Responsável só acessa alunos vinculados.');
    }
    const values = [
      limits.dailyLimit,
      limits.weeklyLimit,
      limits.monthlyLimit,
      limits.orderLimit,
    ];
    if (values.some((value) => value !== null && value < 0)) {
      return failResult('Limite inválido.');
    }
    let updated: CanteenAccount | null = null;
    this.accountsState.update((items) =>
      items.map((item) => {
        if (item.studentId !== studentId) {
          return item;
        }
        updated = { ...item, ...limits };
        return updated;
      }),
    );
    if (!updated) {
      return failResult('Conta de consumo não encontrada.');
    }
    return okResult(updated);
  }

  toggleBlock(studentId: string, productId: string): CanteenResult<boolean> {
    if (this.role() !== 'GUARDIAN' && this.role() !== 'CANTEEN_ADMIN') {
      return failResult('Bloqueio não permitido para este perfil.');
    }
    if (!this.canAccessStudent(studentId)) {
      return failResult('Responsável só acessa alunos vinculados.');
    }
    const exists = this.blocksState().some(
      (item) => item.studentId === studentId && item.productId === productId,
    );
    this.blocksState.update((items) =>
      exists
        ? items.filter(
            (item) =>
              item.studentId !== studentId || item.productId !== productId,
          )
        : [...items, { studentId, productId }],
    );
    return okResult(!exists);
  }

  addRestriction(
    studentId: string,
    ingredient: string,
  ): CanteenResult<CanteenDietaryRestriction> {
    if (this.role() !== 'GUARDIAN' && this.role() !== 'CANTEEN_ADMIN') {
      return failResult('Restrição não permitida para este perfil.');
    }
    if (!this.canAccessStudent(studentId)) {
      return failResult('Responsável só acessa alunos vinculados.');
    }
    const name = ingredient.trim();
    if (!name) {
      return failResult('Informe o ingrediente.');
    }
    const created: CanteenDietaryRestriction = {
      id: createId('rst'),
      studentId,
      ingredient: name,
    };
    this.restrictionsState.update((items) => [...items, created]);
    return okResult(created);
  }

  removeRestriction(restrictionId: string): void {
    const current = this.restrictionsState().find(
      (item) => item.id === restrictionId,
    );
    if (!current || !this.canAccessStudent(current.studentId)) {
      return;
    }
    this.restrictionsState.update((items) =>
      items.filter((item) => item.id !== restrictionId),
    );
  }

  saveCategory(name: string, id?: string): CanteenResult<CanteenCategory> {
    if (!this.canManageCatalog()) {
      return failResult('Sem permissão para alterar o cardápio.');
    }
    const label = name.trim();
    if (!label) {
      return failResult('Informe o nome da categoria.');
    }
    if (id) {
      let updated: CanteenCategory | null = null;
      this.categoriesState.update((items) =>
        items.map((item) => {
          if (item.id !== id) {
            return item;
          }
          updated = { ...item, name: label };
          return updated;
        }),
      );
      if (!updated) {
        return failResult('Categoria não encontrada.');
      }
      return okResult(updated);
    }
    const created: CanteenCategory = {
      id: createId('cat'),
      tenantId: CANTEEN_TENANT_ID,
      schoolId: CANTEEN_SCHOOL_ID,
      canteenId: CANTEEN_ID,
      name: label,
      active: true,
    };
    this.categoriesState.update((items) => [...items, created]);
    return okResult(created);
  }

  saveProduct(dto: SaveCanteenProductDto): CanteenResult<CanteenProduct> {
    if (!this.canManageCatalog()) {
      return failResult('Sem permissão para alterar produtos.');
    }
    if (!dto.name.trim() || dto.price < 0) {
      return failResult('Produto inválido.');
    }
    if (dto.id) {
      let updated: CanteenProduct | null = null;
      this.productsState.update((items) =>
        items.map((item) => {
          if (item.id !== dto.id) {
            return item;
          }
          updated = { ...item, ...dto, id: item.id, name: dto.name.trim() };
          return updated;
        }),
      );
      if (!updated) {
        return failResult('Produto não encontrado.');
      }
      this.audit(
        'PRODUCT_UPDATED',
        'product',
        dto.id,
        `Produto ${dto.name} atualizado.`,
      );
      return okResult(updated);
    }

    const created: CanteenProduct = {
      ...dto,
      id: createId('prd'),
      tenantId: CANTEEN_TENANT_ID,
      schoolId: CANTEEN_SCHOOL_ID,
      canteenId: CANTEEN_ID,
      name: dto.name.trim(),
    };
    this.productsState.update((items) => [...items, created]);
    const slot: CanteenMenuSlot = {
      id: createId('menu'),
      tenantId: CANTEEN_TENANT_ID,
      schoolId: CANTEEN_SCHOOL_ID,
      canteenId: CANTEEN_ID,
      productId: created.id,
      featured: false,
      weekdays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      startsAt: this.unitState().opensAt,
      endsAt: this.unitState().closesAt,
      validFrom: toIsoDate(new Date()),
      validUntil: null,
      availableForScheduling: created.availableForScheduling,
      price: null,
    };
    this.menuState.update((items) => [...items, slot]);
    this.audit(
      'PRODUCT_CREATED',
      'product',
      created.id,
      `Produto ${created.name} criado.`,
    );
    return okResult(created);
  }

  deleteProduct(id: string): CanteenResult<CanteenProduct> {
    if (!this.canManageCatalog()) {
      return failResult('Sem permissão para alterar produtos.');
    }
    const current = this.productsState().find((item) => item.id === id);
    if (!current) {
      return failResult('Produto não encontrado.');
    }
    this.productsState.update((items) =>
      items.filter((item) => item.id !== id),
    );
    this.menuState.update((items) =>
      items.filter((item) => item.productId !== id),
    );
    this.blocksState.update((items) =>
      items.filter((item) => item.productId !== id),
    );
    this.promotionsState.update((items) =>
      items.filter((item) => item.productId !== id),
    );
    this.audit(
      'PRODUCT_DELETED',
      'product',
      id,
      `Produto ${current.name} excluído.`,
    );
    return okResult(current);
  }

  deleteCategory(id: string): CanteenResult<CanteenCategory> {
    if (!this.canManageCatalog()) {
      return failResult('Sem permissão para alterar o cardápio.');
    }
    const current = this.categoriesState().find((item) => item.id === id);
    if (!current) {
      return failResult('Categoria não encontrada.');
    }
    const used = this.productsState().some((item) => item.categoryId === id);
    if (used) {
      return failResult('Há produtos nesta categoria.');
    }
    this.categoriesState.update((items) =>
      items.filter((item) => item.id !== id),
    );
    this.audit(
      'CATEGORY_DELETED',
      'category',
      id,
      `Categoria ${current.name} excluída.`,
    );
    return okResult(current);
  }

  adjustStock(
    productId: string,
    type: Extract<
      CanteenStockMovementType,
      'ENTRY' | 'LOSS' | 'ADJUSTMENT' | 'INVENTORY'
    >,
    quantity: number,
    note: string,
  ): CanteenResult<CanteenProduct> {
    if (!this.canOperate()) {
      return failResult('Sem permissão para movimentar estoque.');
    }
    const product = this.productsState().find((item) => item.id === productId);
    if (!product) {
      return failResult('Produto não encontrado.');
    }
    if (!Number.isFinite(quantity) || quantity < 0) {
      return failResult('Quantidade inválida.');
    }
    const delta =
      type === 'ENTRY' || type === 'ADJUSTMENT'
        ? quantity
        : type === 'LOSS'
          ? -quantity
          : quantity - product.stock;
    if (product.stock + delta < 0) {
      return failResult('Estoque não pode ficar negativo.');
    }
    this.changeStock(productId, delta, type, note.trim() || type);
    const updated =
      this.productsState().find((item) => item.id === productId) ?? null;
    this.audit(
      'STOCK_ADJUSTED',
      'product',
      productId,
      `Estoque de ${product.name} ajustado.`,
    );
    return updated ? okResult(updated) : failResult('Produto não encontrado.');
  }

  openCashier(openingBalance: number): CanteenResult<CanteenCashierSession> {
    if (this.role() !== 'CANTEEN_ADMIN' && this.role() !== 'CANTEEN_MANAGER') {
      return failResult('Somente a gestão abre o caixa.');
    }
    if (this.cashierState()?.status === 'OPEN') {
      return failResult('Já existe um caixa aberto.');
    }
    if (!Number.isFinite(openingBalance) || openingBalance < 0) {
      return failResult('Saldo inicial inválido.');
    }
    const session: CanteenCashierSession = {
      id: createId('cash'),
      tenantId: CANTEEN_TENANT_ID,
      schoolId: CANTEEN_SCHOOL_ID,
      canteenId: CANTEEN_ID,
      status: 'OPEN',
      openedAt: new Date().toISOString(),
      closedAt: null,
      openingBalance: roundMoney(openingBalance),
      openedBy: this.profile().id,
    };
    this.cashierState.set(session);
    this.cashierMovementsState.update((items) => [
      {
        id: createId('cm'),
        sessionId: session.id,
        type: 'OPENING',
        amount: session.openingBalance,
        occurredAt: session.openedAt,
        note: 'Abertura',
      },
      ...items,
    ]);
    this.audit('CASHIER_OPENED', 'cashier', session.id, 'Caixa aberto.');
    return okResult(session);
  }

  closeCashier(): CanteenResult<CanteenCashierSession> {
    if (this.role() !== 'CANTEEN_ADMIN' && this.role() !== 'CANTEEN_MANAGER') {
      return failResult('Somente a gestão fecha o caixa.');
    }
    const session = this.cashierState();
    if (!session || session.status !== 'OPEN') {
      return failResult('Não há caixa aberto.');
    }
    const closed: CanteenCashierSession = {
      ...session,
      status: 'CLOSED',
      closedAt: new Date().toISOString(),
    };
    this.cashierState.set(closed);
    this.audit('CASHIER_CLOSED', 'cashier', session.id, 'Caixa fechado.');
    return okResult(closed);
  }

  addCashierMovement(
    type: Extract<
      CanteenCashierMovementType,
      'ENTRY' | 'WITHDRAWAL' | 'ADJUSTMENT'
    >,
    amount: number,
    note: string,
  ): CanteenResult<CanteenCashierMovement> {
    if (this.role() !== 'CANTEEN_ADMIN' && this.role() !== 'CANTEEN_MANAGER') {
      return failResult('Somente a gestão movimenta o caixa.');
    }
    if (!this.activeCashier()) {
      return failResult('Caixa fechado.');
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      return failResult('Informe um valor maior que zero.');
    }
    const movement = this.pushCashier(
      type,
      roundMoney(amount),
      note.trim() || type,
    );
    return movement ? okResult(movement) : failResult('Caixa fechado.');
  }

  updateSettings(
    patch: Partial<
      Pick<
        CanteenUnit,
        | 'name'
        | 'description'
        | 'status'
        | 'opensAt'
        | 'closesAt'
        | 'allowsSale'
        | 'allowsScheduling'
        | 'allowsBalance'
        | 'allowsCounterSale'
        | 'allowsPickup'
        | 'allowsStudentOrders'
        | 'minimumSchedulingAdvanceHours'
        | 'cancellationWindowHours'
      >
    >,
  ): CanteenResult<CanteenUnit> {
    if (this.role() !== 'CANTEEN_ADMIN') {
      return failResult('Somente o administrador altera a cantina.');
    }
    if (
      patch.minimumSchedulingAdvanceHours !== undefined &&
      patch.minimumSchedulingAdvanceHours < 0
    ) {
      return failResult('Antecedência inválida.');
    }
    this.unitState.update((current) => ({ ...current, ...patch }));
    return okResult(this.unitState());
  }

  canManageCatalog(): boolean {
    const role = this.role();
    return role === 'CANTEEN_ADMIN' || role === 'CANTEEN_MANAGER';
  }

  canOperate(): boolean {
    const role = this.role();
    return (
      role === 'CANTEEN_ADMIN' ||
      role === 'CANTEEN_MANAGER' ||
      role === 'CANTEEN_OPERATOR'
    );
  }

  canManageCashier(): boolean {
    const role = this.role();
    return role === 'CANTEEN_ADMIN' || role === 'CANTEEN_MANAGER';
  }

  salesReport(from: string, to: string): CanteenReportRow[] {
    return this.groupOrders(from, to, (order) =>
      order.items.map((item) => ({
        label: item.productName,
        quantity: item.quantity,
        total: item.total,
      })),
    );
  }

  salesByCategory(from: string, to: string): CanteenReportRow[] {
    return this.groupOrders(from, to, (order) =>
      order.items.map((item) => {
        const product = this.productsState().find(
          (entry) => entry.id === item.productId,
        );
        return {
          label: this.categoryName(product?.categoryId ?? ''),
          quantity: item.quantity,
          total: item.total,
        };
      }),
    );
  }

  salesByStudent(from: string, to: string): CanteenReportRow[] {
    return this.groupOrders(from, to, (order) => [
      {
        label: this.studentName(order.studentId),
        quantity: 1,
        total: order.total,
      },
    ]);
  }

  salesByClass(from: string, to: string): CanteenReportRow[] {
    return this.groupOrders(from, to, (order) => {
      const student = this.studentsState().find(
        (item) => item.id === order.studentId,
      );
      return [
        {
          label: this.className(student?.classId ?? ''),
          quantity: 1,
          total: order.total,
        },
      ];
    });
  }

  private purchaseContext(dto: PlaceCanteenOrderDto, now: Date) {
    const at = dto.scheduledFor ? new Date(dto.scheduledFor) : now;
    const quoted = this.quoteLines(dto.lines, at).map((line) => ({
      product: line.product,
      slot: this.menuSlotFor(line.product.id, at),
      quantity: line.quantity,
      unitPrice: line.unitPrice,
    }));
    const account = this.accountFor(dto.studentId);
    return {
      now,
      scheduledFor: dto.scheduledFor ? new Date(dto.scheduledFor) : null,
      paymentMethod: dto.paymentMethod,
      channel: dto.channel,
      student:
        this.studentsState().find((item) => item.id === dto.studentId) ?? null,
      account,
      canteen: this.unitState(),
      cashierOpen: this.cashierState()?.status === 'OPEN',
      lines: quoted,
      blockedProductIds: new Set(this.blockedIds(dto.studentId)),
      restrictedIngredients: this.restrictionsState()
        .filter((item) => item.studentId === dto.studentId)
        .map((item) => item.ingredient),
      spend: consumptionSpend(
        this.transactionsState(),
        dto.studentId,
        dto.scheduledFor ? new Date(dto.scheduledFor) : now,
      ),
    };
  }

  private pushBalance(
    studentId: string,
    type: CanteenTransactionType,
    amount: number,
    description: string,
    orderId: string | null,
  ): CanteenAccount | null {
    const current = this.accountFor(studentId);
    if (!current) {
      return null;
    }
    const delta = type === 'DEBIT' ? -amount : amount;
    const balance = roundMoney(current.balance + delta);
    const updated: CanteenAccount = { ...current, balance };
    this.accountsState.update((items) =>
      items.map((item) => (item.id === updated.id ? updated : item)),
    );
    const transaction: CanteenTransaction = {
      id: createId('txn'),
      tenantId: CANTEEN_TENANT_ID,
      schoolId: CANTEEN_SCHOOL_ID,
      canteenId: CANTEEN_ID,
      accountId: updated.id,
      studentId,
      type,
      amount: roundMoney(amount),
      balanceAfter: balance,
      description,
      occurredAt: new Date().toISOString(),
      orderId,
    };
    this.transactionsState.update((items) => [transaction, ...items]);
    if (type === 'DEBIT') {
      this.audit('BALANCE_DEBITED', 'account', updated.id, description);
    }
    return updated;
  }

  private changeStock(
    productId: string,
    delta: number,
    type: CanteenStockMovementType,
    note: string,
  ): void {
    this.productsState.update((items) =>
      items.map((item) =>
        item.id === productId
          ? { ...item, stock: roundMoney(item.stock + delta) }
          : item,
      ),
    );
    const movement: CanteenStockMovement = {
      id: createId('stk'),
      tenantId: CANTEEN_TENANT_ID,
      schoolId: CANTEEN_SCHOOL_ID,
      canteenId: CANTEEN_ID,
      productId,
      type,
      quantity: Math.abs(delta),
      occurredAt: new Date().toISOString(),
      note,
    };
    this.stockState.update((items) => [movement, ...items]);
  }

  private pushCashier(
    type: CanteenCashierMovementType,
    amount: number,
    note: string,
  ): CanteenCashierMovement | null {
    const session = this.cashierState();
    if (!session || session.status !== 'OPEN') {
      return null;
    }
    const movement: CanteenCashierMovement = {
      id: createId('cm'),
      sessionId: session.id,
      type,
      amount,
      occurredAt: new Date().toISOString(),
      note,
    };
    this.cashierMovementsState.update((items) => [movement, ...items]);
    return movement;
  }

  private replaceOrder(order: CanteenOrder): void {
    this.ordersState.update((items) =>
      items.map((item) => (item.id === order.id ? order : item)),
    );
  }

  private audit(
    action: string,
    entity: string,
    entityId: string,
    description: string,
  ): void {
    const entry: CanteenAuditLog = {
      id: createId('aud'),
      tenantId: CANTEEN_TENANT_ID,
      schoolId: CANTEEN_SCHOOL_ID,
      userId: this.profile().id,
      action,
      entity,
      entityId,
      timestamp: new Date().toISOString(),
      description,
    };
    this.auditState.update((items) => [entry, ...items]);
  }

  private pushNotice(title: string, body: string): void {
    this.noticesState.update((items) => [
      {
        id: createId('ntc'),
        title,
        body,
        createdAt: new Date().toISOString(),
        role: 'ALL',
      },
      ...items,
    ]);
  }

  private buildCashier(): CanteenCashierSnapshot {
    const session = this.cashierState();
    const movements = session
      ? this.cashierMovementsState().filter(
          (item) => item.sessionId === session.id,
        )
      : [];
    const sum = (type: CanteenCashierMovementType) =>
      roundMoney(
        movements
          .filter((item) => item.type === type)
          .reduce((total, item) => total + item.amount, 0),
      );
    const entries = sum('ENTRY');
    const sales = sum('SALE');
    const refunds = sum('REFUND');
    const withdrawals = sum('WITHDRAWAL');
    const adjustments = sum('ADJUSTMENT');
    return {
      session,
      movements,
      entries,
      sales,
      refunds,
      withdrawals,
      adjustments,
      expectedBalance: expectedCashierBalance({
        openingBalance: session?.openingBalance ?? 0,
        entries,
        sales,
        refunds,
        withdrawals,
        adjustments,
      }),
    };
  }

  private buildStats(): CanteenOperationStats {
    const now = new Date();
    const today = toIsoDate(now);
    const month = today.slice(0, 7);
    const counted = this.ordersState().filter((order) =>
      [
        'PAID',
        'SCHEDULED',
        'PREPARING',
        'READY',
        'READY_FOR_PICKUP',
        'DELIVERED',
      ].includes(order.status),
    );
    const salesToday = roundMoney(
      counted
        .filter((order) => toIsoDate(new Date(order.createdAt)) === today)
        .reduce((total, order) => total + order.total, 0),
    );
    const monthOrders = counted.filter((order) =>
      toIsoDate(new Date(order.createdAt)).startsWith(month),
    );
    const salesMonth = roundMoney(
      monthOrders.reduce((total, order) => total + order.total, 0),
    );
    const ordersToday = this.ordersState().filter(
      (order) => toIsoDate(new Date(order.createdAt)) === today,
    );
    return {
      salesToday,
      salesMonth,
      ordersToday: ordersToday.length,
      averageTicket:
        monthOrders.length === 0
          ? 0
          : roundMoney(salesMonth / monthOrders.length),
      pendingOrders: this.ordersState().filter((order) =>
        ['PAID', 'PREPARING', 'PENDING_PAYMENT', 'CREATED'].includes(
          order.status,
        ),
      ).length,
      scheduledOrders: this.ordersState().filter(
        (order) => order.status === 'SCHEDULED',
      ).length,
      pickupOrders: this.ordersState().filter((order) =>
        ['READY', 'READY_FOR_PICKUP'].includes(order.status),
      ).length,
      cancellations: this.ordersState().filter(
        (order) => order.status === 'CANCELLED',
      ).length,
      refunds: this.ordersState().filter((order) => order.status === 'REFUNDED')
        .length,
      lowStock: this.lowStockProducts().length,
    };
  }

  private groupOrders(
    from: string,
    to: string,
    project: (
      order: CanteenOrder,
    ) => readonly { label: string; quantity: number; total: number }[],
  ): CanteenReportRow[] {
    const map = new Map<string, CanteenReportRow>();
    for (const order of this.ordersForSession()) {
      const day = toIsoDate(new Date(order.createdAt));
      if (
        day < from ||
        day > to ||
        order.status === 'CANCELLED' ||
        order.status === 'REFUNDED'
      ) {
        continue;
      }
      for (const row of project(order)) {
        const current = map.get(row.label) ?? {
          label: row.label,
          quantity: 0,
          total: 0,
        };
        map.set(row.label, {
          label: row.label,
          quantity: current.quantity + row.quantity,
          total: roundMoney(current.total + row.total),
        });
      }
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }
}

function createId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}
